/* vendorForecastFile.js -- Step 6's "fill vendor forecasts from a file" (2026-10-29, Brian): the parsed vendor-forecast
 * rows (utils/parseVendorForecastExcel.js) MATCHED to the staged Step-6 vendors and turned into (a) the change list the
 * screen shows BEFORE anything is applied, and (b) the vendor objects Apply hands to the Forecast Wizard's own host save,
 * AdminPage step6SaveForecastRows (assertVendorRouted -> the fold).
 *
 * ONE PATH, NOT A SECOND ONE. Each matched row is applied the way an operator would apply it in the Set Vendor Forecasts
 * grid: the grid's row for that vendor (utils/forecastWizardRows.buildWizardRow), the file's answers written into that
 * row's cells, the grid's buildVendorObj. The change list is the diff between the staged row and the SAME fold Apply runs
 * (utils/step6ForecastFold.foldWizardObjIntoVendor), so it cannot show something Apply does not do.
 *
 * NEVER GUESSED. A row finds its vendor by vendor_name (trimmed, case and inner spaces ignored) + its line
 * (buildVendorIsLineKey(is_category, gl_line_name), the vendor rule) against the staged vendor's own route (vendorRoute).
 * No vendor, two vendors, two rows for one vendor, a value the grid cannot express, or a prepaid vendor whose contracts
 * book a forecast -> the row is NAMED with its reason and not applied. The prepaid contracts are never read for writing:
 * this module returns vendor objects only.
 *
 * Plain ASCII only. */
import { buildVendorIsLineKey } from './isLineKey';
import { vendorContext, buildWizardRow, buildVendorObj } from './forecastWizardRows';
import { foldWizardObjIntoVendor } from './step6ForecastFold';
import { vendorTypeLabel, seatBasisLabel } from './vendorForecastTypes';
import { FREQ_OPTIONS } from './compCycle';
import { deptSlug } from './seatDepartments';
import { parsePercentText, percentText } from './percentText';
import { PCT_REVENUE_TOTAL, PCT_REVENUE_TOTAL_LABEL, PCT_RATE_MESSAGES } from './pctRevenueSource';
import { formatMonthLabel } from '../engines/dateUtils';

const nameKey = (s) => String(s == null ? '' : s).trim().toLowerCase().replace(/\s+/g, ' ');
const money = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthText = (m) => (m === 'until_canceled' ? 'Until Canceled' : (m ? formatMonthLabel(m, 'shortFull') : '-'));
const freqLabel = (f) => (FREQ_OPTIONS.find(o => o.value === f) || {}).label || f || 'Monthly';

/**
 * @param {Array}  fileRows  parseVendorForecastExcel(...).rows
 * @param {object} c
 *   staged          step6Vendors (the snake rows the import reads)
 *   wizardRows      step6ForecastVendorRows (the same rows as the grid sees them: id = index, name, isCat, monthlyData)
 *   forecastMonths, actualsMonths, closeMonth, firstForecast
 *   seatDeptOptions [{ id, label }] -- what the grid offers (buildSeatDeptResolver(...).options)
 *   pctSourceOptions [{ key, label }] -- what the grid offers ([] before Step 7)
 *   prepaidByIdx    Map(staged index -> [contract]) -- the contracts the import links to each staged vendor
 *   lineLabel       (lineKey) -> its label, for the screen
 * @returns {{ changed, unchanged, refused, objs }}
 */
export function planVendorForecastFile(fileRows, c) {
  const { staged, wizardRows, forecastMonths, actualsMonths, closeMonth, firstForecast } = c;
  const seatOpts = c.seatDeptOptions || [];
  const pctOpts = c.pctSourceOptions || [];
  const lineLabel = c.lineLabel || (k => k);
  const fm = forecastMonths || [];
  const refused = [], matched = [];

  // -- 1. match every row (by name, then by line) -----------------------------------------------------------------
  const byName = new Map();
  (wizardRows || []).forEach(w => { const k = nameKey(w.name); if (!byName.has(k)) byName.set(k, []); byName.get(k).push(w); });
  for (const f of fileRows || []) {
    const line = buildVendorIsLineKey(f.is_category, f.gl_line_name);
    const base = { row: f.row, vendor: f.vendor_name, line: line || null, lineText: `${f.is_category} / ${f.gl_line_name}` };
    if (!line) { refused.push({ ...base, reason: `the line "${f.gl_line_name}" under "${f.is_category}" is not a vendor line name.` }); continue; }
    if (!f.forecast_type) { refused.push({ ...base, reason: `"${f.forecast_type_raw}" is not a forecast type.` }); continue; }
    const sameName = byName.get(nameKey(f.vendor_name)) || [];
    if (!sameName.length) { refused.push({ ...base, reason: `no staged vendor is named "${f.vendor_name}".` }); continue; }
    const hits = sameName.filter(w => w.isCat === line);
    if (!hits.length) {
      const on = [...new Set(sameName.map(w => w.isCat ? lineLabel(w.isCat) : '(no line)'))].join(', ');
      refused.push({ ...base, reason: `"${f.vendor_name}" is staged on ${on}, not on ${lineLabel(line)}.` }); continue;
    }
    if (hits.length > 1) { refused.push({ ...base, reason: `${hits.length} staged vendors are named "${f.vendor_name}" on ${lineLabel(line)} -- which one is meant cannot be told.` }); continue; }
    matched.push({ f, w: hits[0], base });
  }
  // two rows for one vendor: neither is applied (which one is meant cannot be told)
  const perVendor = new Map();
  matched.forEach(m => perVendor.set(m.w.id, (perVendor.get(m.w.id) || 0) + 1));
  const single = [];
  for (const m of matched) {
    if (perVendor.get(m.w.id) > 1) {
      const rowsOf = matched.filter(x => x.w.id === m.w.id).map(x => x.f.row).join(', ');
      refused.push({ ...m.base, reason: `rows ${rowsOf} all name this vendor -- none of them is applied.` });
    } else single.push(m);
  }

  // -- 2. each matched row -> the grid's row with the file's answers -> the grid's vendor object -> the fold ------------
  const changed = [], unchanged = [], objs = [];
  const firstOfCalMonth = (mm) => fm.find(m => Number(m.split('-')[1]) === mm) || null;
  const inWindow = (m) => fm.includes(m);
  for (const { f, w, base } of single) {
    const v = staged[w.id];
    const refuse = (reason) => refused.push({ ...base, reason });
    if (!v) { refuse('the staged vendor could not be read.'); continue; }
    const loaded = v.forecast_type || 'constant';
    if (loaded === 'skip') { refuse('the vendor is marked Skip at Step 6 (it is not imported).'); continue; }
    if (loaded === 'prepaid') {
      const fc = (c.prepaidByIdx && c.prepaidByIdx.get(w.id)) || [];
      const forecastCells = fc.filter(p => Object.entries(p.amort || {}).some(([m, a]) => (closeMonth ? m > closeMonth : inWindow(m)) && Number(a) !== 0));
      if (forecastCells.length) {
        refuse(`it is a prepaid vendor and ${forecastCells.length} of its contracts book${forecastCells.length === 1 ? 's' : ''} a forecast -- set its forecast on the Prepaid sub-step.`);
        continue;
      }
    }
    const t = f.forecast_type;
    // the months the grid can express: a forecast month (Start, End), or Until Canceled
    const start = f.start_month || firstForecast;
    const end = f.end_month || 'until_canceled';
    if (!inWindow(start)) { refuse(`start_month ${f.start_month} is not a forecast month (${fm[0]} to ${fm[fm.length - 1]}).`); continue; }
    if (end !== 'until_canceled' && !inWindow(end)) { refuse(`end_month ${end} is not a forecast month (${fm[0]} to ${fm[fm.length - 1]}).`); continue; }
    if (end !== 'until_canceled' && end < start) { refuse(`end_month ${end} is before start_month ${start}.`); continue; }

    const ctx = vendorContext(w, actualsMonths || []);
    const row = buildWizardRow(w, { ctx, rule: 'three_mo_avg', cutoff: closeMonth || '', firstForecast, hasSourceOptions: pctOpts.length > 0, pctRevenueSourceOptions: pctOpts });
    row.type = t;
    row.startDate = start;
    row.endDate = end;
    if (t === 'constant') {
      row.amount = f.amount == null ? 0 : f.amount;
      row.freq = f.frequency || 'monthly';
      if (row.freq === 'monthly') {
        if (f.billing_month) { refuse('billing_month is for a non-Monthly frequency (a Monthly payment books every month).'); continue; }
      } else if (f.billing_month) {
        const mm = Number(f.billing_month.split('-')[1]);
        const first = firstOfCalMonth(mm);
        if (!first || first !== f.billing_month) { refuse(`billing_month ${f.billing_month}: a non-Monthly payment books from the first forecast month of its calendar month${first ? ` (${first})` : ''}.`); continue; }
        row.anchorMM = mm;
      }
    } else if (t === 'per_seat') {
      if (!(f.per_seat_cost > 0)) { refuse('a Per Seat row needs a per_seat_cost above 0.'); continue; }
      row.perSeatCost = f.per_seat_cost;
      row.seatBasis = f.seat_basis || 'fte';
      const ids = [], unknown = [];
      for (const d of f.seat_departments || []) {
        const o = seatOpts.find(x => x.id === deptSlug(d) || String(x.label).trim().toLowerCase() === d.toLowerCase());
        if (o) { if (!ids.includes(o.id)) ids.push(o.id); } else unknown.push(d);
      }
      if (unknown.length) { refuse(`seat_departments: ${unknown.map(d => `"${d}"`).join(', ')} ${unknown.length === 1 ? 'is not a department' : 'are not departments'} of this company (${seatOpts.map(o => o.label).join(', ') || 'none set up'}).`); continue; }
      row.seatDepartments = ids;
    } else if (t === 'pct_revenue') {
      if (!pctOpts.length) { refuse('% of Revenue needs the revenue lines Step 7 creates -- set it on the Vendor Dashboard after go-live.'); continue; }
      const pr = parsePercentText(f.pct_rate == null ? '' : String(f.pct_rate), PCT_RATE_MESSAGES);
      if (pr.error) { refuse(`pct_rate: ${pr.error}`); continue; }
      const b = String(f.pct_base || '').trim();
      const src = b.toLowerCase() === PCT_REVENUE_TOTAL_LABEL.toLowerCase() ? PCT_REVENUE_TOTAL
        : ((pctOpts.find(o => String(o.label).trim().toLowerCase() === b.toLowerCase()) || {}).key || null);
      if (!src) { refuse(`pct_base "${b}" is not "${PCT_REVENUE_TOTAL_LABEL}" or a revenue line of this company.`); continue; }
      row.pctRate = pr.rate; row.pctText = percentText(pr.rate); row.pctRevenueSource = src;
    }
    // one_off: the type alone (the grid writes one_off config-only)
    const obj = buildVendorObj(w, row, fm);
    const next = foldWizardObjIntoVendor(v, obj, { firstForecast, closeMonth, forecastMonths: fm });
    const changes = diffFold(v, next, t, { firstForecast, forecastMonths: fm, closeMonth, seatOpts, pctOpts });
    const entry = { ...base, vendor: w.name, idx: w.id, lineText: lineLabel(base.line), type: t, changes, obj };
    if (changes.length) { changed.push(entry); objs.push(obj); } else unchanged.push(entry);
  }
  refused.sort((a, b) => a.row - b.row);
  return { changed, unchanged, refused, objs };
}

/* What the fold changes on one staged row, field by field, in the operator's words. Only the fields the row's (new) type
 * forecasts from, plus the type; the forecast months are counted (they follow from the fields above, and a stale series
 * would otherwise hide behind an unchanged amount). */
export function diffFold(v, next, t, { firstForecast, forecastMonths, closeMonth, seatOpts, pctOpts }) {
  const out = [];
  const add = (field, before, after) => { if (before !== after) out.push({ field, before, after }); };
  const typeText = (x) => (x === 'prepaid' ? 'Prepaid' : vendorTypeLabel(x || 'constant'));
  add('Type', typeText(v.forecast_type), typeText(t));
  const deptText = (ids) => ((ids && ids.length) ? ids.map(id => (seatOpts.find(o => o.id === id) || {}).label || id).join(', ') : 'Company-wide');
  const pctBase = (k) => (k == null ? '-' : k === PCT_REVENUE_TOTAL ? PCT_REVENUE_TOTAL_LABEL : ((pctOpts.find(o => o.key === k) || {}).label || k));
  if (t === 'constant') {
    add('Amount', money(v.monthly_amount), money(next.monthly_amount));
    add('Frequency', freqLabel(v.billing_frequency || 'monthly'), freqLabel(next.billing_frequency));
    if (next.billing_frequency !== 'monthly') add('Billing month', monthText(v.billing_anchor || null), monthText(next.billing_anchor));
  } else if (t === 'per_seat') {
    add('Cost per seat', money(v.per_seat_cost), money(next.per_seat_cost));
    add('Seat basis', seatBasisLabel(v.seat_basis || 'fte'), seatBasisLabel(next.seat_basis));
    add('Departments', deptText(v.seat_departments), deptText(next.seat_departments));
  } else if (t === 'pct_revenue') {
    add('% of Revenue', `${percentText(v.pct_rate || 0) || '0'}%`, `${percentText(next.pct_rate) || '0'}%`);
    add('Revenue base', pctBase(v.pct_revenue_source), pctBase(next.pct_revenue_source));
  }
  add('Start', monthText(v.start_date || firstForecast), monthText(next.start_date || firstForecast));
  add('End', monthText(v.end_date || 'until_canceled'), monthText(next.end_date || 'until_canceled'));
  const a = v.monthly_amounts || {}, b = next.monthly_amounts || {};
  const isFc = (m) => (closeMonth ? m > closeMonth : forecastMonths.includes(m));
  const months = [...new Set([...Object.keys(a), ...Object.keys(b)])].filter(isFc);
  const n = months.filter(m => !(m in a && m in b && Number(a[m]) === Number(b[m]))).length;
  if (n) out.push({ field: 'Forecast months', before: `${months.filter(m => m in a).length} stored`, after: `${n} of ${months.length} change` });
  return out;
}
